import React, { useRef, useState } from 'react';
import { Text, Icon, MoreActions, Tooltip } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { IMoreActionItem } from '@ui-library/MoreActions/MoreActions.types';
import { FileText, Loader } from 'lucide-react';
import type { Artifact } from '../RequirementGroups.types';
import { formatArtifactTitle } from '../../../utils/parseArtifacts';
import { Permissions } from '../../../constants';

interface ArtifactItemProps {
  artifact: Artifact;
  onSelect: () => void;
  onOpenInNewTab: () => void;
  onDownload: () => void;
  onRename: () => void;
  handleShareArtifact: (artifact: Artifact) => void;
  onDelete: () => void;
  isDeleting: boolean;
  isDownloading: boolean;
  permissions: string[];
}

export const ArtifactItem: React.FC<ArtifactItemProps> = ({
  artifact,
  onSelect,
  onOpenInNewTab,
  onDownload,
  onRename,
  handleShareArtifact,
  onDelete,
  isDeleting,
  isDownloading,
  permissions,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);

  // Check if user has update permission
  const hasUpdatePermission = permissions.includes(Permissions.UpdateProjectPermission);
  const hasDownloadPermission = permissions.includes(Permissions.DownloadPermission);

  const menuItems: IMoreActionItem[] = [
    {
      id: 'open-in-new-tab',
      label: 'Open in New Tab',
      onClick: () => {
        onOpenInNewTab();
        setMenuOpen(false);
      },
    },
    ...(hasDownloadPermission ? [{
      id: 'download',
      label: isDownloading ? 'Downloading...' : 'Download',
      onClick: () => {
        onDownload();
        setMenuOpen(false);
      },
      disabled: isDownloading as boolean,
    }] : []),
    ...(hasUpdatePermission ? [
      {
        id: 'separator',
        label: '',
        type: 'separator' as const,
      },
      {
        id: 'rename',
        label: 'Rename',
        onClick: () => {
          onRename();
          setMenuOpen(false);
        },
      },
      {
        id: 'share',
        label: 'Share',
        onClick: () => {
          handleShareArtifact(artifact);
          setMenuOpen(false);
        },
      },
      {
        id: 'delete',
        label: isDeleting ? 'Deleting...' : 'Delete',
        onClick: () => {
          onDelete();
          setMenuOpen(false);
        },
        disabled: isDeleting as boolean,
      }
    ] : [])
  ];

  const displayTitle = formatArtifactTitle(artifact.title, artifact.version);

  return (
    <div className="flex items-center gap-bg-v2-sm px-bg-v2-md py-bg-v2-xs rounded-bg-v2-sm transition-colors duration-200 relative group hover:bg-bg-secondary">
      {/* Artifact Button */}
      <Tooltip content={displayTitle} position="right">
        <button 
          onClick={onSelect} 
          className="flex items-center gap-bg-v2-sm flex-1 min-w-0 bg-transparent border-none cursor-pointer p-0 text-left"
        >
          {/* File Icon */}
          <div className="flex items-center justify-center flex-shrink-0">
            <FileText size={16} className="text-text-tertiary" />
          </div>

          {/* Artifact Title */}
          <Text 
            variant="text-sm" 
            color="secondary" 
            className="flex-1 min-w-0 line-clamp-2 break-words leading-[1.4] text-left"
          >
            {displayTitle}
          </Text>
        </button>
      </Tooltip>

      {/* More Actions Menu */}
      <div className="relative flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <button
          ref={menuRef}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          className="flex items-center justify-center w-6 h-6 rounded-bg-v2-md bg-transparent border-none cursor-pointer text-text-tertiary hover:bg-bg-secondary hover:text-text-secondary transition-all duration-200 disabled:cursor-not-allowed"
          disabled={isDeleting}
          aria-label="More options"
        >
          {isDeleting ? (
            <Loader className="animate-spin" size={16} />
          ) : (
            <Icon name={IconName.More} size="sm" variant="secondary" />
          )}
        </button>

        <MoreActions
          items={menuItems}
          isOpen={menuOpen}
          onClose={() => setMenuOpen(false)}
          position="bottom-right"
          triggerRef={menuRef}
        />
      </div>
    </div>
  );
};

